import React from 'react'
import { Badge, Tile, TileCaption, TileOverlay } from '@mc/design-system'
import { useTranslation } from '@mc/i18n'
import { paths } from '@mc/paths'
import Image from 'components/LegacyImage'
import Link from 'components/Link/CustomLink'
import { ContentType, CourseMeta, OutcomeContent } from 'types/data'

const getTileLink = (content: OutcomeContent, courseMeta: CourseMeta) => {
  if (content.contentType === ContentType.CHAPTER) {
    return paths.chaptersWatch(courseMeta.courseSlug, content.slug)
  }

  if (content.contentType === ContentType.MODULE) {
    return paths.sessionsClassHome(content.courseSlug)
  }

  if (content.contentType === ContentType.SUBCHAPTER) {
    return paths.chaptersWatch(courseMeta.courseSlug, content.chapterSlug)
  }

  return ''
}

export const LessonContentTile = ({
  content,
  courseMeta,
  isPrioritized,
  onClick,
}: {
  content: OutcomeContent
  courseMeta: CourseMeta
  isPrioritized: boolean
  onClick: () => void
}) => {
  const { t } = useTranslation('@mc/next')

  const getDurationString = (duration: number) => {
    const totalMinutes = duration / 60
    const hours = Math.floor(totalMinutes / 60)
    const mins = Math.floor(totalMinutes % 60)

    return [
      t('outcomePage.tile.lessonInfoPill.separator'),
      hours
        ? t('outcomePage.tile.lessonInfoPill.hour', {
            numberOfHours: hours,
          })
        : '',
      mins
        ? t('outcomePage.tile.lessonInfoPill.min', {
            numberOfMinutes: mins,
          })
        : '',
    ].join(' ')
  }

  return (
    <Tile>
      <TileCaption position='right top' padding={2}>
        <Badge kind='dim'>
          <span className='mc-text--capitalize'>
            {t('outcomePage.tile.lessonInfoPill.type')}
            {content.durationInSecs &&
              getDurationString(content.durationInSecs)}
          </span>
        </Badge>
      </TileCaption>
      <TileOverlay
        className='mc-corners--3'
        color='25,28,33'
        type='gradient-bottom'
      />
      <TileCaption position='left bottom' padding={4}>
        <p className='mc-text-h5'>{content.title}</p>
      </TileCaption>
      <Link
        ariaHidden
        href={getTileLink(content, courseMeta)}
        tabIndex={-1}
        onClick={onClick}
        passHref
      >
        <Image
          className='mc-corners--3'
          src={content.tileImgSrc}
          alt=''
          priority={isPrioritized}
          width={588}
          height={333}
          objectFit='cover'
        />
      </Link>
    </Tile>
  )
}
